/**
 * Mapbox `Polyline` adapter — renders a multi-segment line through ordered
 * points using a GeoJSON source + line layer. Added in Phase 4 (#325) so the
 * LiveTracking driver-route polyline can sit behind the connector instead of
 * importing `mapbox-gl` directly.
 *
 * Straight-line only — turn-by-turn routing via the Mapbox Directions API
 * stays at the caller (LiveTracking pre-fetches sampled route points). The
 * adapter just draws line segments between consecutive points.
 *
 * Implementation: each Polyline instance manages its own GeoJSON source +
 * line layer keyed by a stable id (so multiple polylines can coexist on the
 * same map). Mount adds the source/layer, unmount removes both. Prop changes
 * call `setData` on the source so the line can update without remount.
 */

import { useEffect, useRef } from 'react';
import type * as React from 'react';
import type { PolylineProps } from '../../types';
import { useMapboxMap } from './MapboxMapView';

let polylineIdCounter = 0;
function nextPolylineId(): string {
  polylineIdCounter += 1;
  return `twm-polyline-${polylineIdCounter}`;
}

export const MapboxPolyline: React.FC<PolylineProps> = ({
  points,
  color = '#0078d4',
  width = 4,
  opacity = 0.75,
}) => {
  const { map, ready } = useMapboxMap();
  const idRef = useRef<string>(nextPolylineId());
  const sourceIdRef = useRef<string>(`${idRef.current}-source`);
  const layerIdRef = useRef<string>(`${idRef.current}-layer`);
  const mountedRef = useRef(false);

  // Mount + unmount. Source/layer creation gated on map.isStyleLoaded() — if
  // the style isn't ready yet, we defer to the next `load` event. Matches the
  // historical LiveTracking pattern (which had the same race).
  useEffect(() => {
    if (!map || !ready) return;
    const sourceId = sourceIdRef.current;
    const layerId = layerIdRef.current;

    const install = () => {
      if (mountedRef.current) return;
      // Remove any stale source/layer from a prior mount cycle (defensive —
      // mapbox-gl is intolerant of double-add).
      if (map.getLayer(layerId)) map.removeLayer(layerId);
      if (map.getSource(sourceId)) map.removeSource(sourceId);

      map.addSource(sourceId, {
        type: 'geojson',
        data: {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: points.map((p) => [p.lng, p.lat]),
          },
        },
      });
      map.addLayer({
        id: layerId,
        type: 'line',
        source: sourceId,
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: {
          'line-color': color,
          'line-width': width,
          'line-opacity': opacity,
        },
      });
      mountedRef.current = true;
    };

    if (map.isStyleLoaded()) {
      install();
    } else {
      map.once('load', install);
    }

    return () => {
      try {
        if (map.getLayer(layerId)) map.removeLayer(layerId);
        if (map.getSource(sourceId)) map.removeSource(sourceId);
      } catch {
        // map may already be torn down — swallow.
      }
      mountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, ready]);

  // Update line geometry on points change without remounting.
  useEffect(() => {
    if (!map || !ready || !mountedRef.current) return;
    const source = map.getSource(sourceIdRef.current) as
      | (ReturnType<typeof map.getSource> & {
          setData: (d: GeoJSON.Feature) => void;
        })
      | undefined;
    if (!source) return;
    source.setData({
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'LineString',
        coordinates: points.map((p) => [p.lng, p.lat]),
      },
    });
  }, [map, ready, points]);

  // Update paint properties (color / width / opacity) without remounting.
  useEffect(() => {
    if (!map || !ready || !mountedRef.current) return;
    const layerId = layerIdRef.current;
    if (!map.getLayer(layerId)) return;
    map.setPaintProperty(layerId, 'line-color', color);
    map.setPaintProperty(layerId, 'line-width', width);
    map.setPaintProperty(layerId, 'line-opacity', opacity);
  }, [map, ready, color, width, opacity]);

  return null;
};
